import { Plus, MapPin, AlertCircle } from 'lucide-react';
import { Button, Skeleton, EmptyState } from '@shopmuchmore/design-system';
import { useShippingAddresses, useSetShippingAddressDefault, useDeleteShippingAddress } from '../shipping.services';
import { ShippingAddressItem } from '../components/ShippingAddressItem';
import { useNavigate } from 'react-router-dom';
import { AccountPageHeader } from '../../account/components/AccountPageHeader';
import { toast } from 'sonner';

export function ShippingAddressListPage() {
  const navigate = useNavigate();
  const { data: addresses, isLoading, isError } = useShippingAddresses();
  const setDefaultMutation = useSetShippingAddressDefault();
  const deleteMutation = useDeleteShippingAddress();

  const handleEdit = (slug: string) => {
    navigate(`/account/addresses/${slug}`);
  };

  const handleSetDefault = (slug: string) => {
    setDefaultMutation.mutate(slug, {
      onSuccess: () => {
        toast.success('Default address updated');
      },
      onError: () => {
        toast.error('Failed to update default address');
      }
    });
  };

  const handleDelete = async (slug: string) => {
    try {
      await deleteMutation.mutateAsync(slug);
      toast.success('Address deleted successfully');
    } catch (error) {
      toast.error('Failed to delete address');
      throw error; // Re-throw to let the dialog handle it if needed
    }
  };

  const handleAddNew = () => {
    navigate('/account/addresses/new');
  };

  if (isLoading) {
    return (
      <div className="w-full space-y-8">
        <AccountPageHeader
          title="Shipping Addresses"
          description="Manage your shipping locations."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-full space-y-8">
        <AccountPageHeader
          title="Shipping Addresses"
          description="Manage your shipping locations."
        />
        <EmptyState
            icon={AlertCircle}
            title="Failed to load addresses"
            description="There was an error fetching your shipping addresses. Please try again later."
            iconClassName="bg-red-50 text-red-500"
            action={
                <Button variant="secondary" className="rounded-full px-8" onClick={() => window.location.reload()}>
                    Retry
                </Button>
            }
        />
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      <AccountPageHeader
        title="Shipping Addresses"
        description="Manage your saved delivery addresses for a faster checkout experience."
        action={
          addresses && addresses.length > 0 ? (
            <Button onClick={handleAddNew} className="w-full md:w-auto gap-2">
              <Plus className="w-4 h-4" />
              Add New
            </Button>
          ) : undefined
        }
      />

      {!addresses || addresses.length === 0 ? (
        <EmptyState
            icon={MapPin}
            title="No addresses saved yet"
            description="You haven't added any shipping addresses yet. Add your first one to speed up your next purchase."
            iconClassName="bg-secondary/5"
            action={
                <Button onClick={handleAddNew} >
                    <Plus className="w-4 h-4" />
                    Add Your First Address
                </Button>
            }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {addresses.map((address) => (
            <ShippingAddressItem
              key={address.shipping_slug}
              address={address}
              onEdit={handleEdit}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              isDeleting={deleteMutation.isPending && deleteMutation.variables === address.shipping_slug}
            />
          ))}
        </div>
      )}
    </div>
  );
}

